import {ISceneAddon} from "../scene/addon/ISceneAddon";
import {Vector3, Mesh, Material, BufferGeometry, Vector2, Object3D, BufferAttribute} from "three";
import {ThreeCSG} from "./ThreeCSG/ThreeCSG";
export class THREEUtils
{
	public static IsWebGLSupported(): boolean
	{
		try
		{
			var canvas = document.createElement("canvas");
			return !!((<any>window).WebGLRenderingContext &&
				(canvas.getContext("webgl2") || canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
			);
		}
		catch (e)
		{
			return false;
		}
	}

	public static flipBufferGeometryNormals(bufferGeometry: BufferGeometry)
	{
		const attributes = bufferGeometry.attributes as {[key: string]: BufferAttribute};

		const tempA = new Vector3();
		const tempB = new Vector3();
		const tempC = new Vector3();
		// flip normals
		for (let i = 0; i < attributes.normal.count; i++)
		{
			tempA.set(
				attributes.normal.getX(i),
				attributes.normal.getY(i),
				attributes.normal.getZ(i)
			);
			tempA.multiplyScalar(-1);
			
			attributes.normal.setXYZ(i, tempA.x, tempB.y, tempC.z);
		}
		attributes.normal.needsUpdate = true;

		// change face winding order
		if (bufferGeometry.index)
		{
			for (let i = 0; i < bufferGeometry.index.count; i += 3)
			{
				//const a = bufferGeometry.index.getX(i);
				const b = bufferGeometry.index.getX(i + 1);
				const c = bufferGeometry.index.getX(i + 2);

				bufferGeometry.index.setX(i + 1, c);
				bufferGeometry.index.setX(i + 2, b);
			}
			bufferGeometry.index.needsUpdate = true;
		}
		else
		{
			for (let i = 0; i < attributes.position.count; i += 3)
			{
				// tempA.set(
				// 	attributes.position.getX(i),
				// 	attributes.position.getY(i),
				// 	attributes.position.getZ(i)
				// );

				tempB.set(
					attributes.position.getX(i + 1),
					attributes.position.getY(i + 1),
					attributes.position.getZ(i + 1)
				);

				tempC.set(
					attributes.position.getX(i + 2),
					attributes.position.getY(i + 2),
					attributes.position.getZ(i + 2)
				);

				attributes.position.setXYZ(i + 1, tempC.x, tempC.y, tempC.z);
				attributes.position.setXYZ(i + 2, tempB.x, tempB.y, tempB.z);
			}
			attributes.position.needsUpdate = true;

			// flip UV coordinates
			for (let i = 0; i < attributes.uv.count; i += 3)
			{
				tempB.set(
					attributes.uv.getX(i + 1),
					attributes.uv.getY(i + 1),
					0
				);

				tempC.set(
					attributes.uv.getX(i + 2),
					attributes.uv.getY(i + 2),
					0
				);
				
				attributes.uv.setXY(i + 1, tempC.x, tempC.y);
				attributes.uv.setXY(i + 2, tempB.x, tempB.y);
			}

			attributes.uv.needsUpdate = true;
		}		
	}

	public static deepClone(object: Object3D)
	{
		const clone = object.clone();
		//
		// we need deepclone -> to make a new copy of everything (like materials and geometry), otherwise it gets messy
		//
		clone.traverse((obj: Object3D) =>
		{
			if (obj instanceof Mesh)
			{
				const material = obj.material;

				if (material instanceof Material)
				{
					obj.material = material.clone();
				}

				obj.geometry = obj.geometry.clone();
			}
		});

		return clone;
	}

	public static subtractNodesFromMeshWithClean(mesh: Mesh, nodes: ISceneAddon[], cleanNormal?: Vector3): Mesh
	{
		mesh.updateMatrix();

		let wallBsp = ThreeCSG.fromMesh(mesh);

		for (let i = 0; i < nodes.length; i++)
		{
			const node = nodes[i];
			const nodeBoundingBox = node.getBoundingBox();
			nodeBoundingBox.updateMatrix();
			const nodeBoundingBoxBsp = ThreeCSG.fromMesh(nodeBoundingBox);

			wallBsp = wallBsp.subtract(nodeBoundingBoxBsp);
		}

		const newWall = ThreeCSG.toMesh(wallBsp, mesh.matrix, mesh.material as Material);

		if (cleanNormal)
		{
			THREEUtils.cleanWallGeometry(newWall.geometry, cleanNormal);
		}

		newWall.quaternion.set(mesh.quaternion.x, mesh.quaternion.y, mesh.quaternion.z, mesh.quaternion.w);
		newWall.position.set(mesh.position.x, mesh.position.y, mesh.position.z);
		newWall.scale.set(mesh.scale.x, mesh.scale.y, mesh.scale.z);

		newWall.castShadow = mesh.castShadow;
		newWall.receiveShadow = mesh.receiveShadow;

		//new_wall.geometry.computeFaceNormals();
		newWall.geometry.computeVertexNormals();
		newWall.geometry.normalizeNormals();

		return newWall;
	}

	private static getArraysetFromBufferAttribute(bufferAttribute: BufferAttribute): number[][]
	{
		const values: number[][] = [];

		for (let i = 0; i < bufferAttribute.count; ++i)
		{
			const vec: number[] = [];
			vec.push(bufferAttribute.getX(i))
			if (bufferAttribute.itemSize > 1)
			{
				vec.push(bufferAttribute.getY(i));
				if (bufferAttribute.itemSize > 2)
				{
					vec.push(bufferAttribute.getZ(i));
					if (bufferAttribute.itemSize > 3)
					{
						vec.push(bufferAttribute.getW(i));
					}
				}
			}
			
			values.push(vec);
		}

		return values;
	}

	/** When creating CSG (or BSP?) by subtracting a box from a plane, the plane is not cut out properly, it will have
	 * some vertices that are off the original plane, and we need to remove those (along with the whole faces that they're part of)
	 * Another solution might be to create boxgeometries instead of planegeometries, but this is probably more efficient
	 * And we don't have to hack-solve the z-fighting issues with the colliding boxes on the walls (inside / outside)
	 */
	public static cleanWallGeometry(geometry: BufferGeometry, normal: Vector3)
	{
		// WallAddon.getBoundingBox() boxDepth is 1.5
		// -> x or z = -0.75

		const positionArray: number[][] = this.getArraysetFromBufferAttribute(geometry.attributes.position as BufferAttribute);
		const uvArray: number[][] = this.getArraysetFromBufferAttribute(geometry.attributes.uv as BufferAttribute);
		const normalArray: number[][] = this.getArraysetFromBufferAttribute(geometry.attributes.normal as BufferAttribute);
		const indexArray: number[][] | null = geometry.index ? this.getArraysetFromBufferAttribute(geometry.index as BufferAttribute) : null;

		const filteredPositionArray: number[][] = [];
		const filteredUvArray: number[][] = [];
		const filteredNormalArray: number[][] = [];
		const filteredIndexArray: number[][] | null = [];
		const faceIndices: number[][] = [];

		if (indexArray)
		{
			for (let i = 0; i < indexArray.length; i += 3)
			{
				faceIndices.push([
					indexArray[i][0],
					indexArray[i + 1][0],
					indexArray[i + 2][0]
				]);
			}
		}
		else
		{
			for (let i = 0; i < positionArray.length; i += 3)
			{
				faceIndices.push([i, i + 1, i + 2]);
			}
		}

		const threshold = 0.1;
		const positionIndicesBlackList: number[] = [];
		for (let i = 0; i < faceIndices.length; ++i)
		{
			const examinedIndices = [
				faceIndices[i][0],
				faceIndices[i][1],
				faceIndices[i][2]
			];

			if (
				Math.abs(positionArray[examinedIndices[0]][2]) > threshold ||
				Math.abs(positionArray[examinedIndices[1]][2]) > threshold ||
				Math.abs(positionArray[examinedIndices[2]][2]) > threshold
			)
			{
				positionIndicesBlackList.push(examinedIndices[0]);
				positionIndicesBlackList.push(examinedIndices[1]);
				positionIndicesBlackList.push(examinedIndices[2]);
			}
		}

		for (let i = 0; i < positionArray.length; ++i)
		{
			if (positionIndicesBlackList.includes(i))
			{
				// filter out position, and all corresponding attributes, that are not part of the plane
			}
			else
			{
				filteredPositionArray.push(positionArray[i]);
				filteredUvArray.push(uvArray[i]);
				filteredNormalArray.push(normalArray[i]);

				if (indexArray)
				{
					filteredIndexArray.push(indexArray[i]);
				}
			}
		}

		geometry.setAttribute("position", new BufferAttribute(new Float32Array(filteredPositionArray.flat()), 3));
		geometry.setAttribute("uv", new BufferAttribute(new Float32Array(filteredUvArray.flat()), 2));
		geometry.setAttribute("normal", new BufferAttribute(new Float32Array(filteredNormalArray.flat()), 3));
		if (indexArray)
		{
			geometry.setIndex(filteredIndexArray.flat());
		}
	}

	public static scaleUVs(bufferGeometry: BufferGeometry, scale: Vector2)
	{
		const uvs = bufferGeometry.getAttribute("uv");

		bufferGeometry.userData.original_uvs = bufferGeometry.userData.original_uvs || THREEUtils.cloneUVs(uvs as BufferAttribute);

		const o_uvs = bufferGeometry.userData.original_uvs as BufferAttribute;

		for (var i = 0; i < uvs.count; i++)
		{
			const originalU = o_uvs.getX(i);
			const originalV = o_uvs.getY(i);

			uvs.setXY(i, originalU * scale.x, originalV * scale.y).needsUpdate = true;
		}
	}

	private static cloneUVs(uvs: BufferAttribute)
	{
		const clone = new BufferAttribute(new Float32Array(uvs.array), 2, false);
		return clone;
	}
}